import { GetLocation, GetLocationCohort } from 'src/graphql/types/delivery';
import { isDefined, naturalSort } from 'src/utils';
import { GScheduledDeliveryTimeSourceEnum } from '../../graphql/types/generated';

export enum RoutingFilter {
  allLocations = 'allLocations',
  automaticallyRouted = 'automaticallyRouted',
  manuallyRouted = 'manuallyRouted',
}

export enum MetrobiFilter {
  All = 'all',
  Eligible = 'eligible',
  Ineligible = 'ineligible',
}

export const metrobiFilterItems = [
  { label: 'All Metrobi Eligibility', value: MetrobiFilter.All },
  { label: 'Eligible', value: MetrobiFilter.Eligible },
  { label: 'Ineligible', value: MetrobiFilter.Ineligible },
];

export enum DeliveryWindowFilter {
  allWindows = 'allWindows',
  nightTime = 'nightTime',
  dayTime = 'dayTime',
}

export const ScheduledDeliveryTimeSourceFilter = Object.freeze({
  ALL_SOURCES: 'ALL_SOURCES' as const,
  ...Object.fromEntries(
    Object.values(GScheduledDeliveryTimeSourceEnum).map((source) => [
      source,
      source,
    ]),
  ),
} as const);

export type Item = {
  value: string;
  label: string;
};

export const alphabeticalByName = (a: GetLocation, b: GetLocation): number =>
  naturalSort(a.businessName, b.businessName);

export const routingFilterItems = [
  { label: 'All Locations', value: RoutingFilter.allLocations },
  { label: 'Automatically Routed', value: RoutingFilter.automaticallyRouted },
  { label: 'Manually Routed', value: RoutingFilter.manuallyRouted },
];

export const allStatesItem = {
  label: 'All States',
  value: 'allStates',
};

export const allCitiesItem = {
  label: 'All Cities',
  value: 'allCities',
};

export const allCohortsItem = {
  label: 'All Cohorts',
  value: 'allCohorts',
};

export const noCohortsItem = {
  label: 'No Cohorts',
  value: 'noCohorts',
};

export const uniqueItems = (
  value: Item,
  index: number,
  self: Item[],
): boolean => index === self.findIndex((t: Item) => t.value === value.value);

export const getFilteredLocations = (
  locations: GetLocation[],
  {
    metrobiFilter = MetrobiFilter.All,
    searchValue,
    routingFilter,
    scheduledDeliveryTimeSourceFilter,
    stateFilter,
    cityFilter,
    zipFilter,
    cohortFilter,
    cohorts,
  }: {
    metrobiFilter?: MetrobiFilter;
    searchValue: string;
    routingFilter: RoutingFilter;
    scheduledDeliveryTimeSourceFilter: string;
    stateFilter: string;
    cityFilter: string;
    zipFilter: string;
    cohortFilter: string;
    cohorts?: GetLocationCohort[];
  },
): GetLocation[] => {
  let myFilteredLocations = [...locations];
  // search filter
  if (searchValue !== '') {
    const foundIds = locations.filter((location: GetLocation): boolean =>
      location.id.includes(searchValue),
    );
    const foundKeyIds = locations.filter((location: GetLocation): boolean =>
      location.keyId.includes(searchValue),
    );
    const foundBusinessNames = locations.filter(
      (location: GetLocation): boolean =>
        location.businessName.toLowerCase().includes(searchValue.toLowerCase()),
    );
    const foundLocations = [...foundIds, ...foundKeyIds, ...foundBusinessNames];
    // remove duplicates
    myFilteredLocations = foundLocations.filter(
      (value, index, self) =>
        index === self.findIndex((t) => t.id === value.id),
    );
  }
  // routing filter
  if (routingFilter !== RoutingFilter.allLocations) {
    if (routingFilter === RoutingFilter.automaticallyRouted) {
      myFilteredLocations = myFilteredLocations.filter(
        (location: GetLocation): boolean =>
          location.includeInAutomatedRouting ?? false,
      );
    } else if (routingFilter === RoutingFilter.manuallyRouted) {
      myFilteredLocations = myFilteredLocations.filter(
        (location: GetLocation): boolean => !location.includeInAutomatedRouting,
      );
    }
  }

  // scheduled delivery time source filter
  if (
    scheduledDeliveryTimeSourceFilter !==
    ScheduledDeliveryTimeSourceFilter.ALL_SOURCES
  ) {
    myFilteredLocations = myFilteredLocations.filter(
      (location: GetLocation): boolean =>
        location.scheduledDeliveryTimeSource ===
        scheduledDeliveryTimeSourceFilter,
    );
  }
  // state filter
  if (stateFilter !== allStatesItem.value) {
    myFilteredLocations = myFilteredLocations.filter(
      (location: GetLocation): boolean =>
        location.address.state === stateFilter,
    );
  }
  // city filter
  if (cityFilter !== allCitiesItem.value) {
    myFilteredLocations = myFilteredLocations.filter(
      (location: GetLocation): boolean => location.address.city === cityFilter,
    );
  }
  // zip filter
  if (zipFilter !== '') {
    myFilteredLocations = myFilteredLocations.filter(
      (location: GetLocation): boolean =>
        location.address.postalCode.includes(zipFilter),
    );
  }
  // cohort filter
  if (
    cohortFilter !== allCohortsItem.value &&
    cohortFilter !== noCohortsItem.value
  ) {
    myFilteredLocations = myFilteredLocations.filter(
      (location: GetLocation): boolean =>
        location.routingCohort?.id === cohortFilter,
    );
  }
  if (cohortFilter === noCohortsItem.value) {
    myFilteredLocations = myFilteredLocations.filter(
      (location: GetLocation): boolean =>
        !isDefined(
          cohorts?.find((cohort) => location.routingCohort?.id === cohort.id),
        ),
    );
  }
  if (metrobiFilter !== MetrobiFilter.All) {
    myFilteredLocations = myFilteredLocations.filter((location) =>
      metrobiFilter === MetrobiFilter.Eligible
        ? location.effectivelyExcluded === false
        : location.effectivelyExcluded === true,
    );
  }
  return myFilteredLocations;
};
